/**
 * Line inspector (W2-06; DESIGN.md §4.11 "LINE", §5.5, §6.7, §6.9, §6.13; rpe-format F§9, F§10). Shows the
 * selected line, or the active line when nothing else is selected:
 *  - name, type (Core.lines.setLineType; data loss goes through DIALOG.lineType), HUD element, image (a non-GIF
 *    image removes GIF progress), text, GIF "Play automatically";
 *  - parent picker that greys out lines that would loop, then DIALOG.parentPrompt; "Also turn with parent" with
 *    its two-frame diagram and sentence; draw order with front / back; "Hide notes under the line" with diagram;
 *    group; tag colour (editor only);
 *  - size fields per kind in friendly units (Length %, Thickness with the real pixels, Image / Text / UI size %,
 *    Brush): no track -> one keyframe at beat 0, one keyframe -> the whole chart, animated -> a keyframe at the
 *    playhead (the hint says which);
 *  - "Uses" chips for features the Guided level does not advertise, the property list with what absent tracks
 *    mean and their fixes (AbsentTrack), and the Advanced group (note tilt, distance effects, RPE-only fields).
 * Guided keeps the first screen short (UX-058): parent, "Also turn with parent", draw order, "Hide notes under the
 * line", group and tag colour sit in a collapsed "More line settings" group (open, with an "in use" badge, when one
 * of them is not at its default), and the property list starts collapsed. A size field whose track is absent is
 * empty with a "Not set (full length)" placeholder instead of a raw default, and its long explanation sits in its
 * info popover (UX-059).
 */

import { Fragment, useCallback, useState } from 'react';
import type { AppServices, InspectorSectionDef, InspectorSectionProps } from '../../../app/registry/types';
import type { LineTypeChoice } from '../../../core/commands/types';
import { Core } from '../../../core/commands/index';
import { Defaults } from '../../../core/model/defaults';
import { isGifAsset, kindOfLine } from '../../../core/model/lineKind';
import { absentMessageKey } from '../../../core/model/props';
import { Units } from '../../../core/model/units';
import { Expr } from '../../../core/expr/index';
import type { ExprProgram } from '../../../core/expr/types';
import type {
  AssetRef,
  ChartDoc,
  DocIndexes,
  Line,
  LineId,
  LineKind,
  LineProp,
  UiElement,
} from '../../../core/model/types';
import { useT } from '../../../i18n/useT';
import type { MessageKey } from '../../../i18n/types';
import { lineTopic, notesTopic } from '../../../state/versions';
import type { DocStore } from '../../../state/types';
import { AbsentTrack } from '../AbsentTrack';
import { ParentPicker } from '../ParentPicker';
import {
  Badge,
  Button,
  Checkbox,
  ColorField,
  Field,
  NumberField,
  Section,
  Select,
  TextArea,
  TextField,
} from '../../../ui';
import { setLineValue } from '../commands';
import { useDocValue, useEditorValue, usePlayheadSec } from '../hooks';
import { LINE_TYPE_ICON, LINE_TYPE_LABEL, propLabelKey, UI_ELEMENT_ICON, UI_ELEMENT_LABEL } from '../labels';
import { changeLineType, changeParent } from '../lineFlows';
import { m } from '../inspector.messages';
import {
  hexToRgb,
  imageAssets,
  keyAtOrBefore,
  lineLabel,
  lineTypeOf,
  lineTypeOptions,
  numberAt,
  onlyLinesSelected,
  parentOptions,
  rgbToHex,
  trackKeys,
  UI_ELEMENT_LIST,
  usedFeatures,
  type ParentOption,
  type UsedFeatures,
} from '../model';
import {
  Callout,
  CoverDiagram,
  fmtNum,
  Readout,
  RotateWithParentDiagram,
  Row,
  Title,
  useSectionBase,
  useStore,
  WithInfo,
  type SectionBase,
} from '../parts';
import s from '../Inspector.module.css';
import { newSeed, playheadBeat, useEditKit, type EditKit } from '../tx';
import { FIELD, linePropField, useFieldProblems } from '../useFieldProblems';
import { unitSetup } from './keyInfo';
import { LineAdvanced } from './LineAdvanced';
import type { BadgeProps } from '../../../ui/types';

// ---------------------------------------------------------------------------------------------------------------
// Derived line data
// ---------------------------------------------------------------------------------------------------------------

export interface LineDerived {
  readonly line: Line;
  readonly kind: LineKind;
  readonly type: LineTypeChoice;
  readonly label: string;
  readonly notes: number;
  readonly used: UsedFeatures;
  readonly parents: readonly ParentOption[];
  readonly groups: ReadonlyArray<{ readonly id: number; readonly name: string }>;
  readonly images: readonly AssetRef[];
  readonly texture: AssetRef | null;
}

function derive(doc: ChartDoc, idx: DocIndexes, id: LineId): LineDerived | null {
  const line = doc.lines.get(id);
  if (line === undefined) return null;
  const kind = kindOfLine(doc, idx, line);
  return {
    line,
    kind,
    type: lineTypeOf(doc, idx, line),
    label: lineLabel(doc, idx, id),
    notes: idx.notesByLine(id).length,
    used: usedFeatures(doc, idx, line),
    parents: parentOptions(doc, idx, [id]),
    groups: [...doc.groups.values()].sort((a, b) => a.order - b.order).map((g) => ({ id: g.id, name: g.name })),
    images: imageAssets(doc),
    texture: line.texture === null ? null : (doc.assets.get(line.texture) ?? null),
  };
}

function sameDerived(a: LineDerived | null, b: LineDerived | null): boolean {
  if (a === null || b === null) return a === b;
  return (
    a.line === b.line &&
    a.kind === b.kind &&
    a.type === b.type &&
    a.label === b.label &&
    a.notes === b.notes &&
    a.texture === b.texture &&
    JSON.stringify(a.used) === JSON.stringify(b.used) &&
    JSON.stringify(a.parents) === JSON.stringify(b.parents) &&
    JSON.stringify(a.groups) === JSON.stringify(b.groups) &&
    a.images.length === b.images.length &&
    a.images.every((x, i) => x === b.images[i])
  );
}

/**
 * Revision of a line's motion (its tracks and keys): the fields that read keyframes during render (size values,
 * text, note tilt, the property list) re-render whenever it changes.
 */
export function useLineRevision(store: DocStore, id: LineId): number {
  const topic = lineTopic(id);
  const selector = useCallback(() => store.version(topic), [store, topic]);
  return useDocValue(store, selector, [topic]);
}

/** Live derived data of a line (re-selected when the line, its tracks, the line list, groups or assets change). */
export function useLineDerived(store: DocStore, id: LineId): LineDerived | null {
  const selector = useCallback((doc: ChartDoc, idx: DocIndexes) => derive(doc, idx, id), [id]);
  return useDocValue(
    store,
    selector,
    [lineTopic(id), notesTopic(id), 'lines', 'lineOrder', 'groups', 'assets'],
    sameDerived,
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Section
// ---------------------------------------------------------------------------------------------------------------

/** The line of the inspector: the one selected line, else the active line when nothing else is selected. */
export function inspectedLine(sel: InspectorSectionProps['selection'], activeLine: LineId | null): LineId | null {
  if (sel.lines.size === 1) return [...sel.lines][0] ?? null;
  if (sel.lines.size === 0 && onlyLinesSelected(sel)) return activeLine;
  return null;
}

export function LineSection(props: InspectorSectionProps) {
  const store = useStore(props.services);
  const activeLine = useEditorValue(props.services, (st) => st.activeLine);
  const id = inspectedLine(props.selection, activeLine);
  if (store === null || id === null) return null;
  return <LineBody services={props.services} store={store} id={id} />;
}

function LineBody(props: { readonly services: AppServices; readonly store: DocStore; readonly id: LineId }) {
  const { services, store, id } = props;
  const t = useT();
  const base = useSectionBase(services, store);
  const d = useLineDerived(store, id);
  useLineRevision(store, id);
  const edit = useEditKit(services, store, `line:${String(id)}`);
  const badges = useFieldProblems(services, { lines: [id] });
  const [seed, setSeed] = useState(newSeed);
  if (d === null) return null;
  const { line, kind, type } = d;
  const uiMover = line.attachUI !== null;
  const typeOptions = lineTypeOptions(base.level, type);
  const advancedInUse = d.used.incline || d.used.controls;
  const layerBadges = badges(FIELD.lineLayers);
  const usedChips = usedChipList(t, d.used, layerBadges.length > 0);
  const textureIsGif = d.texture !== null && isGifAsset(d.texture);

  const patch = (field: string, label: MessageKey, p: Parameters<typeof Core.lines.patchLine>[1]['patch']): void => {
    edit.commit(field, { key: label }, (tx) => {
      Core.lines.patchLine(tx, { id, patch: p });
    });
  };

  // Parent, turning, draw order, cover, group and tag: a collapsed group at Guided (UX-058).
  const moreInUse =
    line.parent !== null || line.zOrder !== 0 || !line.cover || line.group !== null || line.editor.tag !== null;
  const moreFields = (
    <>
      {/* ---------------- Parent */}
      <Row edit={edit} field={FIELD.lineParent}>
        <Field
          label={t(m.lineParent)}
          helpId={FIELD.lineParent}
          help={{ key: m.parentHelp }}
          badges={badges(FIELD.lineParent)}
          info={{ text: { key: m.parentInfo }, rpeTerm: 'father', communityTerm: '父线' }}
        >
          <ParentPicker
            label={t(m.lineParent)}
            value={line.parent === null ? 'none' : String(line.parent)}
            options={d.parents}
            onChange={(parent) => {
              void changeParent(services, store, [id], parent);
            }}
          />
        </Field>
      </Row>
      <Row edit={edit} field={FIELD.lineRotateWithParent}>
        <WithInfo
          info={{ text: m.rotateWithParentSentence, rpe: 'rotateWithFather', illustration: 'rotateWithParent' }}
        >
          <Checkbox
            label={t(m.rotateWithParent)}
            checked={line.rotateWithParent}
            helpId={FIELD.lineRotateWithParent}
            onChange={(on) => {
              edit.once({ key: m.txRotateWithParent }, (tx) => {
                Core.lines.patchLine(tx, { id, patch: { rotateWithParent: on } });
              });
            }}
          />
        </WithInfo>
        <RotateWithParentDiagram on={line.rotateWithParent} />
        <Readout>{t(line.parent === null ? m.rotateNoParent : m.rotateWithParentSentence)}</Readout>
      </Row>

      {/* ---------------- Draw order */}
      <Row edit={edit} field={FIELD.lineZOrder}>
        <WithInfo info={{ text: m.lineZOrderInfo, rpe: 'zOrder' }}>
          <NumberField
            label={t(m.lineZOrder)}
            value={{ kind: 'value', value: line.zOrder }}
            step={1}
            precision={0}
            vars="field"
            randomSeed={seed}
            help={{ key: m.lineZOrderHelp }}
            helpId={FIELD.lineZOrder}
            badges={badges(FIELD.lineZOrder)}
            onCommit={(v) => {
              const z = Math.round(
                v.kind === 'number' ? v.value : (evalSingle(v.program, line.zOrder, seed) ?? line.zOrder),
              );
              if (v.kind === 'expr' && v.program.usesRand) setSeed(newSeed());
              if (z !== line.zOrder) patch(FIELD.lineZOrder, m.txZOrder, { zOrder: z });
            }}
            onScrub={(v, final) => {
              edit.scrub(
                FIELD.lineZOrder,
                { key: m.txZOrder },
                (tx) => {
                  Core.lines.patchLine(tx, { id, patch: { zOrder: Math.round(v) } });
                },
                final,
              );
            }}
          />
        </WithInfo>
        <div className={s.actions}>
          {(['front', 'back'] as const).map((where) => (
            <Button
              key={where}
              size="sm"
              variant="ghost"
              icon={where === 'front' ? 'BringToFront' : 'SendToBack'}
              onClick={() => {
                edit.once({ key: m.txZOrder }, (tx) => {
                  Core.lines.setZExtreme(tx, { ids: [id], where });
                });
              }}
            >
              {t(where === 'front' ? m.zFront : m.zBack)}
            </Button>
          ))}
        </div>
      </Row>

      {/* ---------------- Cover */}
      <Row edit={edit} field={FIELD.lineCover}>
        <WithInfo info={{ text: m.lineCoverInfo, rpe: 'isCover', community: '遮罩', illustration: 'cover' }}>
          <Checkbox
            label={t(m.lineCover)}
            checked={line.cover}
            helpId={FIELD.lineCover}
            onChange={(on) => {
              edit.once({ key: m.txCover }, (tx) => {
                Core.lines.patchLine(tx, { id, patch: { cover: on } });
              });
            }}
          />
        </WithInfo>
        <CoverDiagram on={line.cover} />
        <Readout>{t(m.lineCoverHelp)}</Readout>
        {line.rpe.isCoverRaw !== null && <Readout>{t(m.lineCoverRaw, { raw: line.rpe.isCoverRaw })}</Readout>}
      </Row>

      {/* ---------------- Group, tag */}
      <Row edit={edit} field={FIELD.lineGroup}>
        <Field
          label={t(m.lineGroup)}
          helpId={FIELD.lineGroup}
          info={{ text: { key: m.lineGroupInfo }, rpeTerm: 'Group' }}
        >
          <Select<string>
            ariaLabel={t(m.lineGroup)}
            value={line.group === null ? 'none' : String(line.group)}
            options={[
              { value: 'none', label: t(m.groupDefault) },
              ...d.groups.map((g) => ({ value: String(g.id), label: g.name })),
            ]}
            onChange={(v) => {
              const group = v === 'none' ? null : (Number(v) as NonNullable<Line['group']>);
              edit.once({ key: m.txGroup }, (tx) => {
                Core.lines.patchLine(tx, { id, patch: { group } });
              });
            }}
          />
        </Field>
      </Row>
      <Row edit={edit} field={FIELD.lineTag}>
        <WithInfo info={{ text: m.lineTagInfo }}>
          <ColorField
            className={s.colorStacked}
            label={t(m.lineTag)}
            value={line.editor.tag === null ? null : tagValue(line.editor.tag)}
            allowNone
            helpId={FIELD.lineTag}
            onCommit={(c) => {
              const tag = c === null ? null : rgbToHex(c);
              edit.commit(FIELD.lineTag, { key: m.txTag }, (tx) => {
                Core.lines.patchLineEditor(tx, { id, patch: { tag } });
              });
            }}
          />
        </WithInfo>
        <div className={s.badgeLine}>
          <Badge kind="editorOnly" />
        </div>
      </Row>
    </>
  );

  return (
    <Section title={<Title main={t(m.lineTitle)} detail={d.label} />}>
      <div className={s.fields}>
        {/* ---------------- Name */}
        <Row edit={edit} field={FIELD.lineName}>
          <TextField
            label={t(m.lineName)}
            value={line.name}
            helpId={FIELD.lineName}
            onCommit={(name) => {
              if (name !== line.name) patch(FIELD.lineName, m.txLineName, { name });
            }}
          />
        </Row>

        {/* ---------------- Type */}
        <Row edit={edit} field={FIELD.lineType}>
          <Field
            label={t(m.lineType)}
            helpId={FIELD.lineType}
            badges={badges(FIELD.lineType)}
            info={{ text: { key: m.lineTypeInfo } }}
          >
            <Select<LineTypeChoice>
              ariaLabel={t(m.lineType)}
              value={type}
              options={typeOptions.map((tp) => ({
                value: tp,
                icon: LINE_TYPE_ICON[tp],
                label:
                  base.level === 'guided' && (tp === 'gif' || tp === 'paint' || tp === 'uiMover')
                    ? t(m.typeFull, { label: t(LINE_TYPE_LABEL[tp]) })
                    : t(LINE_TYPE_LABEL[tp]),
              }))}
              onChange={(target) => {
                void changeLineType(services, store, [id], target);
              }}
            />
          </Field>
        </Row>

        {/* ---------------- UI mover */}
        {uiMover && (
          <Row edit={edit} field={FIELD.lineAttachUI}>
            <Field
              label={t(m.lineUiElement)}
              helpId={FIELD.lineAttachUI}
              badges={badges(FIELD.lineAttachUI)}
              info={{ text: { key: m.uiMoverInfo }, rpeTerm: 'attachUI', communityTerm: 'UI 绑定' }}
            >
              <Select<UiElement>
                ariaLabel={t(m.lineUiElement)}
                value={line.attachUI}
                options={UI_ELEMENT_LIST.map((e) => ({
                  value: e,
                  label: t(UI_ELEMENT_LABEL[e]),
                  icon: UI_ELEMENT_ICON[e],
                }))}
                onChange={(element) => {
                  edit.once({ key: m.txAttachUi }, (tx) => {
                    Core.lines.setAttachUI(tx, { line: id, element });
                  });
                }}
              />
            </Field>
            <Readout>{t(m.uiRotationNote)}</Readout>
            {d.notes > 0 && <Callout tone="warning">{t(m.uiMoverNotes)}</Callout>}
          </Row>
        )}

        {/* ---------------- Image */}
        {(kind === 'texture' || kind === 'gif') && (
          <Row edit={edit} field={FIELD.lineTexture}>
            <Field
              label={t(m.lineImage)}
              helpId={FIELD.lineTexture}
              badges={badges(FIELD.lineTexture)}
              info={{ text: { key: m.lineImageInfo }, rpeTerm: 'Texture' }}
            >
              <Select<string>
                ariaLabel={t(m.lineImage)}
                value={line.texture ?? 'mixed'}
                options={[
                  ...(d.texture !== null && !d.images.includes(d.texture)
                    ? [
                        {
                          value: d.texture.id,
                          label: t(m.lineImageMissing, { path: d.texture.path }),
                          icon: 'ImageOff',
                        },
                      ]
                    : []),
                  ...d.images.map((a) => ({ value: a.id, label: a.path, icon: isGifAsset(a) ? 'Film' : 'Image' })),
                  { value: 'add', label: t(m.addImage), icon: 'Plus' },
                ]}
                onChange={(v) => {
                  if (v === 'add') {
                    void services.registry.run('assets.add', { role: 'texture' });
                    return;
                  }
                  const asset = d.images.find((a) => a.id === v)?.id;
                  if (asset === undefined) return;
                  edit.once({ key: m.txTexture }, (tx) => {
                    Core.lines.setTexture(tx, { line: id, asset });
                  });
                }}
              />
            </Field>
            <Readout>{t(m.imageCentred)}</Readout>
            {d.texture !== null && d.texture.mime !== 'image/png' && d.texture.mime !== 'image/jpeg' && (
              <Callout tone="info" testId="image-phira-only">
                {t(m.imagePhiraOnly)}
              </Callout>
            )}
            {kind === 'gif' && <Readout>{t(m.gifRemoveWarn)}</Readout>}
            {kind === 'texture' && textureIsGif && <Readout>{t(m.gifFirstFrame)}</Readout>}
          </Row>
        )}
        {(kind === 'texture' || kind === 'gif') && textureIsGif && (
          <Row edit={edit} field={FIELD.lineGifAutoLoop}>
            <Checkbox
              label={t(m.gifAutoLoop)}
              checked={line.gifAutoLoop}
              helpId={FIELD.lineGifAutoLoop}
              onChange={(on) => {
                edit.once({ key: m.txGifAuto }, (tx) => {
                  Core.lines.setGifAutoLoop(tx, { line: id, on });
                });
              }}
            />
            <Readout>{t(m.gifAutoLoopHelp)}</Readout>
            {(store.idx.tracksOfLine(id).get('gif')?.length ?? 0) > 0 && <Readout>{t(m.gifProgressInUse)}</Readout>}
          </Row>
        )}

        {/* ---------------- Text */}
        {kind === 'text' && <LineText services={services} base={base} edit={edit} line={line} />}

        {base.level === 'full' ? (
          moreFields
        ) : (
          <Section
            title={t(m.moreLineSettings)}
            collapsible
            defaultOpen={moreInUse}
            level={4}
            actions={moreInUse ? <Badge kind="inUse" /> : undefined}
          >
            <div className={s.fields} data-testid="line-more">
              {moreFields}
            </div>
          </Section>
        )}

        {/* ---------------- Size fields per kind */}
        {sizeProps(kind, uiMover).map((spec) => (
          <LineValueField
            key={spec.prop}
            services={services}
            base={base}
            edit={edit}
            line={line}
            kind={kind}
            prop={spec.prop}
            help={spec.help === m.imageSizeHelp ? imageHelp(d.texture) : { key: spec.help }}
            badges={badges(linePropField(spec.prop))}
            seed={seed}
            onRandom={() => {
              setSeed(newSeed());
            }}
          />
        ))}

        {/* ---------------- Uses (also the field of the layer count: checker PH-25 points here) */}
        {usedChips.length > 0 && (
          <div className={s.chips} data-testid="uses" data-field-row={FIELD.lineLayers} data-help-id={FIELD.lineLayers}>
            <span>{t(m.usesLabel)}</span>
            {usedChips.map((chip) => (
              <Fragment key={chip.id}>
                <Badge kind="inUse" label={{ key: m.chip, params: { text: chip.label } }} />
                {chip.id === 'layers' && layerBadges.map((b, i) => <Badge key={`problem-${String(i)}`} {...b} />)}
              </Fragment>
            ))}
            <Button
              size="sm"
              variant="ghost"
              icon="Columns3"
              onClick={() => {
                services.editor.getState().setTimeline({ showMotionColumns: true });
              }}
            >
              {t(m.usesShow)}
            </Button>
          </div>
        )}

        {/* ---------------- Properties */}
        <Section title={t(m.properties)} collapsible defaultOpen={base.level === 'full'} level={4}>
          <AbsentTrack
            full={base.level === 'full'}
            shownAbove={new Set(sizeProps(kind, uiMover).map((x) => x.prop))}
            services={services}
            store={store}
            edit={edit}
            line={line}
            kind={kind}
            hasNotes={d.notes > 0}
            badges={badges}
          />
        </Section>

        {/* ---------------- Advanced */}
        {(base.level === 'full' || advancedInUse) && (
          <Section
            title={t(m.advanced)}
            collapsible
            defaultOpen={base.level === 'guided' && advancedInUse}
            level={4}
            actions={base.level === 'guided' && advancedInUse ? <Badge kind="inUse" /> : <Badge kind="full" />}
          >
            <LineAdvanced services={services} store={store} edit={edit} line={line} kind={kind} badges={badges} />
          </Section>
        )}
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------------------------------------------

function tagValue(tag: string): { readonly kind: 'value'; readonly value: readonly [number, number, number] } | null {
  const rgb = hexToRgb(tag);
  return rgb === null ? null : { kind: 'value', value: rgb };
}

/** The value of a single-value expression (draw order, size fields), null when it gives no number. */
export function evalSingle(prog: ExprProgram, v: number, seed: number): number | null {
  const out = Expr.apply(prog, { v, i: 0, n: 1, u: 0 }, Expr.rng(seed));
  return out !== null && Number.isFinite(out) ? out : null;
}

function imageHelp(texture: AssetRef | null): {
  readonly key: MessageKey;
  readonly params?: Readonly<Record<string, number>>;
} {
  const w = texture?.meta?.width;
  const h = texture?.meta?.height;
  return w !== undefined && h !== undefined
    ? { key: m.imageSizeHelp, params: { w, h } }
    : { key: m.imageSizeHelpNoSize };
}

interface SizeSpec {
  readonly prop: LineProp;
  readonly help: MessageKey;
}

/** The size fields of a line kind (DESIGN.md §6.9). */
export function sizeProps(kind: LineKind, uiMover: boolean): readonly SizeSpec[] {
  if (uiMover)
    return [
      { prop: 'scaleX', help: m.uiSizeHelp },
      { prop: 'scaleY', help: m.uiSizeHelp },
    ];
  switch (kind) {
    case 'normal':
      return [
        { prop: 'scaleX', help: m.lengthHelp },
        { prop: 'scaleY', help: m.thicknessHelp },
      ];
    case 'texture':
    case 'gif':
      return [
        { prop: 'scaleX', help: m.imageSizeHelp },
        { prop: 'scaleY', help: m.imageSizeHelp },
      ];
    case 'text':
      return [
        { prop: 'scaleX', help: m.textSizeHelp },
        { prop: 'scaleY', help: m.textSizeHelp },
      ];
    case 'paint':
      return [
        { prop: 'paint', help: m.brushHelp },
        { prop: 'scaleX', help: m.brushSizeHelp },
        { prop: 'scaleY', help: m.brushSizeHelp },
      ];
  }
}

interface UsedChip {
  readonly id: string;
  readonly label: string;
}

/** The "Uses" chips; the layer chip also shows while a layer problem is reported (its badges sit next to it). */
function usedChipList(t: ReturnType<typeof useT>, used: UsedFeatures, layerProblem: boolean): UsedChip[] {
  const out: UsedChip[] = [];
  const add = (id: string, label: string): void => {
    out.push({ id, label });
  };
  if (used.layers > 1 || layerProblem) add('layers', t(m.usesLayers, { count: used.layers }));
  if (used.color) add('color', t(m.usesColor));
  if (used.scale) add('scale', t(m.usesScale));
  if (used.incline) add('incline', t(m.usesIncline));
  if (used.controls) add('controls', t(m.usesControls));
  if (used.text) add('text', t(m.usesText));
  if (used.paint) add('paint', t(m.usesPaint));
  if (used.gif) add('gif', t(m.usesGif));
  if (used.uiMover) add('uiMover', t(m.usesUiMover));
  return out;
}

/**
 * One number property of a line shown as a single value (DESIGN.md §6.9): the absent default, the one keyframe's
 * value, or the animated value at the playhead; editing follows the "Set" rule of `setLineValue`.
 */
function LineValueField(props: {
  readonly services: AppServices;
  readonly base: SectionBase;
  readonly edit: EditKit;
  readonly line: Line;
  readonly kind: LineKind;
  readonly prop: LineProp;
  readonly help: { readonly key: MessageKey; readonly params?: Readonly<Record<string, number>> };
  readonly badges: readonly BadgeProps[];
  readonly seed: number;
  onRandom(): void;
}) {
  const { services, base, edit, line, kind, prop } = props;
  const t = useT();
  const sec = usePlayheadSec(services);
  const uiMover = line.attachUI !== null;
  const store = base.store;
  const keys = trackKeys(store.doc, store.idx, store.idx.track(line.id, prop, 0));
  const unit = Units.unitFor(prop, { kind, uiMover });
  const setup = unitSetup(t, services, base, unit);
  const absentRaw = Defaults.leadInValue(prop, 0, kind, uiMover) ?? 1;
  const first = keys[0];
  const raw =
    first === undefined
      ? absentRaw
      : keys.length === 1
        ? (first.vOut as number)
        : (numberAt(keys, base.tempo, sec) ?? absentRaw);
  const field = linePropField(prop);
  const label = { key: m.txLineValue, params: { prop: t(propLabelKey(prop, kind, uiMover)) } };
  const write = (rawValue: number) => (tx: Parameters<Parameters<EditKit['once']>[1]>[0]) =>
    setLineValue(tx, { line: line.id, prop, layer: 0, value: rawValue, at: playheadBeat(services, base.tempo) });
  const name = t(propLabelKey(prop, kind, uiMover));
  if (first === undefined) {
    // Absent track: an empty field saying what Phira draws, never a raw default next to "Not set" (UX-059).
    const unitText =
      'unit' in setup.unitProps
        ? setup.unitProps.unit
        : setup.unitProps.unitToggle.mode === 'raw'
          ? setup.unitProps.unitToggle.raw
          : setup.unitProps.unitToggle.friendly;
    const normalLine = !uiMover && kind === 'normal';
    // Short enough for the narrow Guided inspector: "Not set (100 %)" in friendly units, the words in raw units.
    const what =
      normalLine && prop === 'scaleX' && setup.shown === 'raw'
        ? t(m.absentFullLength)
        : normalLine && prop === 'scaleY'
          ? t(m.pxValue, { value: fmtNum(Units.thicknessPx(null), 2) })
          : `${fmtNum(setup.toShown(absentRaw), 3)}${unitText === '' ? '' : ` ${unitText}`}`;
    return (
      <Row edit={edit} field={field}>
        <WithInfo info={{ text: absentMessageKey(prop, kind, uiMover) as MessageKey }}>
          <Field label={name} help={props.help} helpId={field} badges={props.badges}>
            <TextField
              label={name}
              hideLabel
              value=""
              placeholder={{ key: m.notSetPlaceholder, params: { what } }}
              onCommit={(text) => {
                const shown = parseShown(text, unitText);
                if (shown === null) return;
                edit.commit(field, label, write(setup.toRaw(shown)));
              }}
            />
          </Field>
        </WithInfo>
      </Row>
    );
  }
  const hint =
    keys.length === 1 ? t(m.staticHint) : t(m.animatedHint, { beat: base.fmtBeat(playheadBeat(services, base.tempo)) });
  return (
    <Row edit={edit} field={field}>
      <NumberField
        label={name}
        value={{ kind: 'value', value: setup.toShown(raw) }}
        step={setup.step}
        precision={3}
        vars="field"
        randomSeed={props.seed}
        help={props.help}
        helpId={field}
        badges={props.badges}
        {...setup.unitProps}
        onCommit={(v) => {
          let shown: number | null;
          if (v.kind === 'number') shown = v.value;
          else {
            shown = evalSingle(v.program, setup.toShown(raw), props.seed);
            if (v.program.usesRand) props.onRandom();
          }
          if (shown === null) return;
          edit.commit(field, label, write(setup.toRaw(shown)));
        }}
        onScrub={(v, final) => {
          edit.scrub(field, label, write(setup.toRaw(v)), final);
        }}
      />
      <Readout testId={`hint-${prop}`}>
        {unit === 'thickness'
          ? t(m.readoutPair, { first: hint, second: t(m.thicknessPx, { px: fmtNum(Units.thicknessPx(raw), 2) }) })
          : hint}
      </Readout>
    </Row>
  );
}

/** A number typed into an absent size field ("100", "100 %", "1.5x"), or null when it is not one. */
export function parseShown(text: string, unit: string): number | null {
  let src = text.trim();
  if (src === '') return null;
  if (unit !== '' && src.toLowerCase().endsWith(unit.toLowerCase())) src = src.slice(0, -unit.length).trim();
  const v = Number(src);
  return Number.isFinite(v) ? v : null;
}

/** Text of a text line: the one keyframe, or the keyframe active at the playhead (edits key at the playhead). */
function LineText(props: {
  readonly services: AppServices;
  readonly base: SectionBase;
  readonly edit: EditKit;
  readonly line: Line;
}) {
  const { services, base, edit, line } = props;
  const t = useT();
  usePlayheadSec(services);
  const store = base.store;
  const keys = trackKeys(store.doc, store.idx, store.idx.track(line.id, 'text', 0));
  const at = playheadBeat(services, base.tempo);
  const current = keys.length <= 1 ? keys[0] : keyAtOrBefore(keys, at);
  const value = typeof current?.vOut === 'string' ? current.vOut : '';
  return (
    <Row edit={edit} field={FIELD.lineText}>
      <WithInfo info={{ text: m.lineTextInfo, rpe: 'textEvents' }}>
        <TextArea
          label={t(m.lineText)}
          value={value}
          rows={2}
          helpId={FIELD.lineText}
          onCommit={(text) => {
            if (text === value) return;
            edit.commit(FIELD.lineText, { key: m.txText }, (tx) =>
              setLineValue(tx, { line: line.id, prop: 'text', layer: 0, value: text, at }),
            );
          }}
        />
      </WithInfo>
      {keys.length > 1 && <Readout>{t(m.lineTextAnimated, { count: keys.length, beat: base.fmtBeat(at) })}</Readout>}
    </Row>
  );
}

export const lineSectionDef: InspectorSectionDef = {
  id: 'line',
  order: 300,
  level: 'guided',
  appliesTo: (sel, services) => inspectedLine(sel, services.editor.getState().activeLine) !== null,
  component: LineSection,
};
